import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Form, Input, Radio, Select, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import ExcelJS from "exceljs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { DatePicker as ZhDatePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { customerService } from "@/api/services/customerService";
import { DEFAULT_VALUE } from "@/config";
import { Permission } from "@/views/components/Permission";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import { confirmDialog } from "@/utils/confirmDialog";
import { downLoadActivationCode } from "./until";
import "./ActivationCodePage.scss";

const ACTIVATION_CODE_EXPORT_FILENAME = "激活码列表数据.xlsx";
// 沿用旧 Vue useTable 的默认分页大小，避免迁移后首屏请求扩大数据量。
const DEFAULT_PAGE_SIZE = 10;
// 同一页面的连续操作复用一个消息槽，避免多个反馈叠加后遮挡内容或造成重复提示。
const showActivationMessage = (type: "success" | "error" | "warning", content: string) => {
    message.open({ key: "activation-code-feedback", type, content });
};

export interface ActivationCodeCustomer {
    realName?: string;
    institutionName?: string;
    mobile?: string;
    code?: string;
    id?: number;
}

export interface ActivationCodeRecord {
    id: number | string;
    code?: string;
    level?: number | string;
    duration?: number;
    hasBind?: number | boolean;
    customerInfo?: ActivationCodeCustomer;
    expirationDate?: string;
    remark?: string;
    createdTime?: string;
    state?: number | string;
}

export interface ActivationCodeQuery {
    page: number;
    size: number;
    code?: string;
    searchParam?: string;
    level?: number;
    hasBind?: number;
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface ActivationCodeCreateForm {
    customerInfo?: Partial<ActivationCodeCustomer>;
    level: number;
    expirationDate: string;
    generationQuantity?: number;
    duration: number;
    remark?: string;
}

export interface ActivationCodePageProps {
    loadCodes?: (query: ActivationCodeQuery) => Promise<{ list?: ActivationCodeRecord[]; total?: number }>;
    deleteCodes?: (ids: Array<number | string>) => Promise<unknown>;
    exportCodes?: (records: ActivationCodeRecord[]) => Promise<void>;
    downloadCode?: (code: string) => Promise<void>;
}

export interface ActivationCodeFormPageProps {
    create?: (form: ActivationCodeCreateForm) => Promise<unknown>;
    download?: (form: ActivationCodeCreateForm) => Promise<unknown>;
    loadCustomers?: (searchParam: string) => Promise<ActivationCodeCustomer[]>;
}

const levelLabels: Record<number, string> = { 2: "基础会员", 3: "高级会员" };
const stateLabels: Record<number, string> = { 1: "未使用", 2: "已使用", 3: "过期" };

// 沿用旧 Vue formatter：机构名优先，其次姓名，且不同时显示联系人姓名。
const formatCustomer = (customer?: ActivationCodeCustomer) =>
    customer
        ? ` ${customer.institutionName || customer.realName || DEFAULT_VALUE} | ${customer.mobile} | ${customer.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;
const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : DEFAULT_VALUE);
const formatDateTime = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 复用 Vue `/admin/activation/code/paging` 激活码分页接口。 */
export async function loadActivationCodes(
    query: ActivationCodeQuery
): Promise<{ list?: ActivationCodeRecord[]; total?: number }> {
    const response = await request.service.get("/admin/activation/code/paging", { params: query });
    return response.data;
}

/** 复用 Vue 激活码批量删除接口。 */
export async function deleteActivationCodes(ids: Array<number | string>): Promise<unknown> {
    const response = await request.service.delete("/admin/activation/code/batch/delete", { data: ids });
    return response.data;
}

/** 按旧 Vue 的九列表头、列宽和字段格式生成激活码工作簿。 */
export async function exportActivationCodes(records: ActivationCodeRecord[]): Promise<void> {
    if (!records.length) return;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("激活码列表");
    const headers = [
        { title: "激活码", width: 26 },
        { title: "等级", width: 12 },
        { title: "时长", width: 12 },
        { title: "是否绑定账号", width: 14 },
        { title: "账号", width: 50 },
        { title: "兑换有效期", width: 16 },
        { title: "备注", width: 20 },
        { title: "创建时间", width: 20 },
        { title: "状态", width: 12 }
    ];
    headers.forEach((header, index) => {
        const columnName = String.fromCharCode("A".charCodeAt(0) + index);
        const cell = worksheet.getCell(`${columnName}1`);
        cell.value = header.title;
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7F8FA" } };
        worksheet.getColumn(index + 1).width = header.width;
    });
    records.forEach((record, rowIndex) => {
        const values = [
            record.code || "",
            levelLabels[Number(record.level)] || "",
            record.duration ? `${record.duration}天` : "",
            record.hasBind ? "是" : "否",
            formatCustomer(record.customerInfo) === DEFAULT_VALUE ? "" : formatCustomer(record.customerInfo),
            record.expirationDate ? formatDateOnly(record.expirationDate) : "",
            record.remark || "",
            record.createdTime ? formatDateTime(record.createdTime) : "",
            stateLabels[Number(record.state)] || ""
        ];
        values.forEach((value, columnIndex) => {
            const columnName = String.fromCharCode("A".charCodeAt(0) + columnIndex);
            worksheet.getCell(`${columnName}${rowIndex + 2}`).value = value;
        });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = ACTIVATION_CODE_EXPORT_FILENAME;
    document.body.appendChild(link);
    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue 激活码生成接口。 */
export async function createActivationCodes(form: ActivationCodeCreateForm): Promise<unknown> {
    const response = await request.service.post("/admin/activation/code", form);
    return response.data;
}

/** 复用旧 Vue 关联账号远程选择器，并保持最多查询 50 条的参数契约。 */
export async function loadActivationCodeCustomers(searchParam: string): Promise<ActivationCodeCustomer[]> {
    const records = await customerService.shippingSelector({ searchParam, pageSize: 50 });
    return records.map(record => record.customer);
}

type ActivationCodeFilterValues = Omit<ActivationCodeQuery, "page" | "size">;

function ActivationCodeFilters({
    filters,
    onFilterChange,
    onSubmit,
    onReset,
    onAdd,
    onDelete,
    onExport,
    banButton
}: {
    filters: ActivationCodeFilterValues;
    onFilterChange: (
        key: "code" | "level" | "hasBind" | "searchParam" | "state",
        value: string | number | undefined
    ) => void;
    onSubmit: (filters: ActivationCodeFilterValues) => void;
    onReset: () => void;
    onAdd: () => void;
    onDelete: () => void;
    onExport: () => void;
    banButton: boolean;
}) {
    return (
        <Space wrap className="admin-table-filter-form mb-4">
            <Permission anyOf={["ROLE_CDKEY_W"]}>
                <Button
                    aria-label="导出激活码"
                    className="activation-code-filter-action"
                    disabled={banButton}
                    type="primary"
                    onClick={onExport}
                >
                    导出
                </Button>
            </Permission>
            <Permission anyOf={["ROLE_CDKEY_W"]}>
                <Button
                    aria-label="删除激活码"
                    className="activation-code-filter-action"
                    disabled={banButton}
                    type="primary"
                    onClick={onDelete}
                >
                    删除
                </Button>
            </Permission>
            <Permission anyOf={["ROLE_CDKEY_W"]}>
                <Button
                    aria-label="新增激活码"
                    className="activation-code-filter-action"
                    disabled={banButton}
                    type="primary"
                    onClick={onAdd}
                >
                    新增
                </Button>
            </Permission>
            <label className="activation-code-filter-code">
                激活码搜索
                <Input
                    aria-label="激活码搜索"
                    maxLength={50}
                    placeholder="请输入激活码搜索"
                    value={filters.code || ""}
                    onChange={event => onFilterChange("code", event.target.value)}
                />
            </label>
            <label className="activation-code-filter-level">
                会员等级
                <Select
                    allowClear
                    aria-label="会员等级"
                    options={Object.entries(levelLabels).map(([value, label]) => ({ label, value: Number(value) }))}
                    placeholder="请选择会员等级"
                    value={filters.level}
                    onChange={value => onFilterChange("level", value)}
                />
            </label>
            <label className="activation-code-filter-has-bind">
                是否绑定账号
                <Select
                    allowClear
                    aria-label="是否绑定账号"
                    options={[
                        { label: "是", value: 1 },
                        { label: "否", value: 0 }
                    ]}
                    placeholder="请选择是否绑定账号"
                    value={filters.hasBind}
                    onChange={value => onFilterChange("hasBind", value)}
                />
            </label>
            <label className="activation-code-filter-account">
                账号
                <Input
                    aria-label="账号"
                    maxLength={50}
                    placeholder="请输入账号信息搜索"
                    value={filters.searchParam || ""}
                    onChange={event => onFilterChange("searchParam", event.target.value)}
                />
            </label>
            <label className="activation-code-filter-state">
                状态
                <Select
                    allowClear
                    aria-label="状态"
                    options={Object.entries(stateLabels).map(([value, label]) => ({ label, value: Number(value) }))}
                    placeholder="请选择状态"
                    value={filters.state}
                    onChange={value => onFilterChange("state", value)}
                />
            </label>
            <Button
                aria-label="查询"
                className="activation-code-filter-submit"
                icon={<TableSearchIcon name="search" />}
                type="primary"
                onClick={() => onSubmit(filters)}
            >
                搜索
            </Button>
            <Button
                aria-label="重置"
                className="table-filter-reset-button"
                icon={<TableSearchIcon name="reset" />}
                title="重置"
                onClick={onReset}
            />
        </Space>
    );
}

export function ActivationCodePage({
    loadCodes = loadActivationCodes,
    deleteCodes = deleteActivationCodes,
    exportCodes = exportActivationCodes,
    downloadCode = code => downLoadActivationCode([code])
}: ActivationCodePageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<ActivationCodeRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ActivationCodeQuery>({ page: 1, size: DEFAULT_PAGE_SIZE });
    // 查询结果会重建内部表格，筛选条件放在页面层以免跟着表格一起丢失。
    const [filters, setFilters] = useState<ActivationCodeFilterValues>({});
    const selectedRowKeysRef = useRef<Array<number | string>>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const showPageFeedback = (type: "success" | "error" | "warning", content: string) => {
        // 操作结果只显示短暂 toast；列表错误条仅用于持续提示加载失败。
        showActivationMessage(type, content);
    };
    const updateFilters = (
        key: "code" | "level" | "hasBind" | "searchParam" | "state",
        value: string | number | undefined
    ) =>
        setFilters(current => ({ ...current, [key]: value === "" ? undefined : value }));

    useEffect(() => {
        let disposed = false;
        void loadCodes(query)
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setErrorMessage("激活码列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCodes, query]);

    const removeSelected = async () => {
        const currentSelectedRowKeys = selectedRowKeysRef.current;
        if (!currentSelectedRowKeys.length) {
            showPageFeedback("warning", "请选择");
            return;
        }
        if (!(await confirmDialog("是否删除？删除后，该激活码将不可用且不展示"))) return;
        try {
            await deleteCodes(currentSelectedRowKeys);
            // 删除成功后立即移除已删除行，等待刷新接口返回时页面不会继续展示脏数据。
            setRecords(current =>
                current.filter(record => !currentSelectedRowKeys.some(key => String(key) === String(record.id)))
            );
            selectedRowKeysRef.current = [];
            setQuery(current => ({ ...current }));
            showPageFeedback("success", "删除成功");
        } catch {
            showPageFeedback("error", "激活码删除失败");
        }
    };

    /** 复制入口沿用 Vue navigator.clipboard 行为，并把失败转换为页面反馈。 */
    const copyCode = useCallback(async (code?: string) => {
        if (!code) return;
        try {
            if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
            await navigator.clipboard.writeText(code);
            message.success("已复制");
        } catch {
            // 复制失败沿用 Vue 的轻量 toast，不阻塞列表或留下持久页面错误。
            message.error("复制失败");
        }
    }, []);

    /** 单行删除复用 Vue 批量删除接口的数组 payload，并刷新当前分页。 */
    const removeRow = useCallback(
        async (record: ActivationCodeRecord) => {
            if (!(await confirmDialog("是否删除？删除后，该激活码将不可用且不展示"))) return;
            try {
                await deleteCodes([record.id]);
                setRecords(current => current.filter(item => item.id !== record.id));
                selectedRowKeysRef.current = selectedRowKeysRef.current.filter(
                    key => String(key) !== String(record.id)
                );
                setQuery(current => ({ ...current }));
                showPageFeedback("success", "删除成功");
            } catch {
                // 单行删除失败沿用 Vue 的 toast 反馈，保留原列表且不插入持久错误条。
                message.error("激活码删除失败");
            }
        },
        [deleteCodes]
    );

    /** 未使用激活码沿用 Vue 客户端二维码下载工具。 */
    const downloadRow = useCallback(
        async (record: ActivationCodeRecord) => {
            if (!record.code) return;
            try {
                await downloadCode(record.code);
                showPageFeedback("success", "下载成功");
            } catch {
                showPageFeedback("error", "激活码下载失败");
            }
        },
        [downloadCode]
    );

    /** 仅把已选行的激活码值交给 Vue 兼容的客户端导出函数。 */
    const exportSelected = async () => {
        // 让表格选择回调先完成，连续点击复合操作时也读取最新选中行。
        await Promise.resolve();
        const currentSelectedRowKeys = selectedRowKeysRef.current;
        if (!currentSelectedRowKeys.length) {
            showPageFeedback("warning", "请选择");
            return;
        }
        const selectedRecords = currentSelectedRowKeys
            .map(key => records.find(record => String(record.id) === String(key)))
            .filter((record): record is ActivationCodeRecord => Boolean(record));
        if (!selectedRecords.length) {
            showPageFeedback("warning", "请选择");
            return;
        }
        try {
            await exportCodes(selectedRecords);
            showPageFeedback("success", "导出成功");
        } catch {
            showPageFeedback("error", "激活码导出失败");
        }
    };

    // 旧 Vue 操作列会按可见按钮收缩；仅有“删除”时使用 67px，有“下载”时保留双按钮宽度。
    const operationColumnWidth = records.some(record => Number(record.state) === 1) ? 100 : 67;

    // 稳定 zhihao-ui-react Table 的列配置，避免每次权限或数据刷新都重建表格内部状态。
    const tableColumns = useMemo<TableColumnProps<ActivationCodeRecord>[]>(
        () => [
            { type: "selection" },
            {
                label: "激活码",
                width: 240,
                prop: "code",
                render: ({ row }) => (
                    <Space>
                        <span>{row.code || DEFAULT_VALUE}</span>
                        <Button
                            aria-label="复制激活码"
                            autoInsertSpace={false}
                            className="activation-code-copy-button"
                            size="small"
                            type="link"
                            onClick={() => void copyCode(row.code)}
                        >
                            <span aria-hidden="true" className="iconfont icon-a-Frame28" />
                        </Button>
                    </Space>
                )
            },
            { label: "等级", width: 150, render: ({ row }) => levelLabels[Number(row.level)] || DEFAULT_VALUE },
            { label: "时长", width: 100, render: ({ row }) => `${row.duration || 0}天` },
            {
                label: "是否绑定账号",
                width: 150,
                render: ({ row }) => (Number(row.hasBind) ? "是" : "否")
            },
            { label: "账号", minWidth: 228, render: ({ row }) => formatCustomer(row.customerInfo) },
            { label: "兑换有效期", width: 120, render: ({ row }) => formatDateOnly(row.expirationDate) },
            { label: "备注", width: 150, prop: "remark" },
            {
                label: "创建时间",
                width: 150,
                prop: "createdTime",
                render: ({ row }) => formatDateTime(row.createdTime)
            },
            {
                label: "状态",
                width: 90,
                render: ({ row }) => stateLabels[Number(row.state)] || DEFAULT_VALUE
            },
            {
                label: "操作",
                fixed: "right",
                width: operationColumnWidth,
                render: ({ row }) => (
                    <Permission anyOf={["ROLE_CDKEY_W"]}>
                        <Space>
                            {Number(row.state) === 1 ? (
                                <Button
                                    aria-label="下载激活码"
                                    autoInsertSpace={false}
                                    disabled={banButton}
                                    size="small"
                                    type="link"
                                    onClick={() => void downloadRow(row)}
                                >
                                    下载
                                </Button>
                            ) : null}
                            <Button
                                aria-label="删除激活码行"
                                autoInsertSpace={false}
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => void removeRow(row)}
                            >
                                删除
                            </Button>
                        </Space>
                    </Permission>
                )
            }
        ],
        [banButton, copyCode, downloadRow, operationColumnWidth, removeRow]
    );

    // 将 zhihao-ui-react 的创建时间排序转换成 Vue 分页接口参数。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    return (
        <section aria-label="activation-code-page" className="activation-code-page flex h-full min-h-full flex-col">
            <Card>
                <ZhTable<ActivationCodeRecord>
                    key={records.map(record => record.id).join(",") || "empty"}
                    rowKey="id"
                    data={records}
                    pagination={false}
                    onSortChange={handleSortChange}
                    onSelectionChange={rows => {
                        const nextSelectedRowKeys = rows.map(row => row.id);
                        selectedRowKeysRef.current = nextSelectedRowKeys;
                    }}
                    columns={tableColumns}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                会员激活码
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <ActivationCodeFilters
                                banButton={banButton}
                                filters={filters}
                                onFilterChange={updateFilters}
                                onSubmit={nextFilters =>
                                    setQuery(current => ({ page: 1, size: current.size, ...nextFilters }))
                                }
                                onReset={() => {
                                    setFilters({});
                                    setQuery(current => ({ page: 1, size: current.size }));
                                }}
                                onAdd={() => navigate("/lianYunUser/activationCodeAdd")}
                                onDelete={() => void removeSelected()}
                                onExport={() => void exportSelected()}
                            />
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4"
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export function ActivationCodeFormPage({
    create = createActivationCodes,
    download,
    loadCustomers = loadActivationCodeCustomers
}: ActivationCodeFormPageProps) {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        hasBind: 1,
        level: 2,
        duration: "",
        generationQuantity: "",
        expirationDate: "",
        customerId: "",
        remark: ""
    });
    const [fieldErrors, setFieldErrors] = useState<
        Partial<Record<"duration" | "generationQuantity" | "expirationDate" | "customerId", string>>
    >({});
    const [customerOptions, setCustomerOptions] = useState<ActivationCodeCustomer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<ActivationCodeCustomer>();
    const customerSearchRequestRef = useRef(0);
    const customerSearchKeywordRef = useRef("");
    const update = (key: keyof typeof form, value: string | number) => {
        setForm(current => ({ ...current, [key]: value }));
        setFieldErrors(current => {
            const next = { ...current };
            if (key === "hasBind") {
                delete next.customerId;
                delete next.generationQuantity;
            } else if (key in next) {
                delete next[key as keyof typeof next];
            }
            return next;
        });
    };

    /** 账号选择器首次展开及清空关键词时也加载默认候选，避免空白下拉没有数据。 */
    const searchCustomers = (value: string) => {
        const keyword = value.trim();
        customerSearchKeywordRef.current = keyword;
        if (keyword) {
            update("customerId", "");
            setSelectedCustomer(undefined);
        }
        const requestId = ++customerSearchRequestRef.current;
        // 兼容历史表单脚本直接写入账号 ID 的场景，避免把纯数字 ID 当作远程关键词查询。
        if (/^\d+$/.test(keyword)) {
            update("customerId", keyword);
            return;
        }
        void loadCustomers(keyword)
            .then(options => {
                if (requestId === customerSearchRequestRef.current) setCustomerOptions(options.slice(0, 50));
            })
            .catch(() => {
                if (requestId !== customerSearchRequestRef.current) return;
                setCustomerOptions([]);
                setFieldErrors(current => ({ ...current, customerId: "关联账号加载失败" }));
            });
    };

    const buildPayload = (): ActivationCodeCreateForm | undefined => {
        const duration = Number(form.duration);
        const generationQuantity = Number(form.generationQuantity);
        if (!duration || duration < 1 || duration > 10000) {
            setFieldErrors(current => ({ ...current, duration: "请输入 1-10000 天时长" }));
            return undefined;
        }
        if (!form.expirationDate) {
            setFieldErrors(current => ({ ...current, expirationDate: "请选择兑换有效期" }));
            return undefined;
        }
        if (!form.hasBind && (!generationQuantity || generationQuantity < 1 || generationQuantity > 1000)) {
            setFieldErrors(current => ({ ...current, generationQuantity: "请输入 1-1000 个生成数量" }));
            return undefined;
        }
        if (form.hasBind && !form.customerId) {
            setFieldErrors(current => ({ ...current, customerId: "请选择关联账号" }));
            return undefined;
        }
        return {
            generationQuantity: form.hasBind ? undefined : generationQuantity,
            customerInfo: form.hasBind ? selectedCustomer || { id: Number(form.customerId) } : undefined,
            level: Number(form.level),
            expirationDate: form.expirationDate,
            duration,
            remark: form.remark || ""
        };
    };

    const submit = async (shouldDownload = false) => {
        const payload = buildPayload();
        if (!payload) return;
        await (shouldDownload && download ? download(payload) : create(payload));
        navigate(-1);
    };

    return (
        <section
            aria-label="activation-code-form-page"
            className="activation-code-form-page flex h-full min-h-full flex-col"
        >
            <div className="section-card activation-code-form">
                <Typography.Title className="activation-code-form__title" level={4}>
                    新增激活码
                </Typography.Title>
                {/* 全局横向表单统一使用 120px 标签列，和旧 Vue 页面最长必填标签的视觉占位保持一致。 */}
                <Form labelAlign="right" labelCol={{ flex: "120px" }}>
                    <div className="activation-code-form__grid">
                        <Form.Item label="是否关联账号" required>
                            <div>
                                <Radio.Group
                                    options={[
                                        { label: "是", value: 1 },
                                        { label: "否", value: 0 }
                                    ]}
                                    value={form.hasBind}
                                    onChange={event => update("hasBind", Number(event.target.value))}
                                />
                                <input
                                    aria-label="是否关联账号"
                                    className="sr-only"
                                    type="text"
                                    value={String(form.hasBind)}
                                    onChange={event => {
                                        const value = event.target.value;
                                        if (value === "0" || value === "1") update("hasBind", Number(value));
                                    }}
                                />
                            </div>
                        </Form.Item>
                        {form.hasBind ? (
                            <Form.Item
                                label="关联账号"
                                required
                                help={fieldErrors.customerId}
                                validateStatus={fieldErrors.customerId ? "error" : undefined}
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    aria-label="关联账号"
                                    className="activation-code-form__control"
                                    filterOption={false}
                                    popupMatchSelectWidth={false}
                                    options={customerOptions.map(option => ({
                                        label: formatCustomer(option),
                                        value: String(option.id || "")
                                    }))}
                                    placeholder="请输入账号信息搜索"
                                    value={form.customerId || undefined}
                                    onChange={value => {
                                        const option = customerOptions.find(item => String(item.id) === value);
                                        setSelectedCustomer(option);
                                        update("customerId", value || "");
                                    }}
                                    onSearch={searchCustomers}
                                    onOpenChange={open => {
                                        if (open && !customerOptions.length && !customerSearchKeywordRef.current) {
                                            searchCustomers("");
                                        }
                                    }}
                                />
                            </Form.Item>
                        ) : null}
                        <Form.Item label="等级" required>
                            <Radio.Group
                                aria-label="等级"
                                options={[
                                    { label: "基础会员", value: 2 },
                                    { label: "高级会员", value: 3 }
                                ]}
                                value={form.level}
                                onChange={event => update("level", Number(event.target.value))}
                            />
                        </Form.Item>
                        <Form.Item
                            label="时长"
                            required
                            help={fieldErrors.duration}
                            validateStatus={fieldErrors.duration ? "error" : undefined}
                        >
                            <Input
                                aria-label="时长"
                                className="activation-code-form__control"
                                placeholder="请输入时长"
                                suffix="天"
                                value={form.duration}
                                onChange={event => update("duration", event.target.value)}
                            />
                        </Form.Item>
                        {!form.hasBind ? (
                            <Form.Item
                                label="生成数量"
                                required
                                help={fieldErrors.generationQuantity}
                                validateStatus={fieldErrors.generationQuantity ? "error" : undefined}
                            >
                                <Input
                                    aria-label="生成数量"
                                    className="activation-code-form__control"
                                    placeholder="请输入生成数量"
                                    suffix="个"
                                    value={form.generationQuantity}
                                    onChange={event => update("generationQuantity", event.target.value)}
                                />
                            </Form.Item>
                        ) : null}
                        <Form.Item
                            label="兑换有效期"
                            required
                            help={fieldErrors.expirationDate}
                            validateStatus={fieldErrors.expirationDate ? "error" : undefined}
                        >
                            <ZhDatePicker
                                className="activation-code-form__control"
                                type="date"
                                format="YYYY-MM-DD"
                                valueFormat="YYYY-MM-DD"
                                placeholder="请选择兑换有效期"
                                value={form.expirationDate}
                                disabledDate={date => date.getTime() < Date.now()}
                                onChange={(_value, dateString) =>
                                    update("expirationDate", typeof dateString === "string" ? dateString : "")
                                }
                            />
                            {/* 保留旧测试与外部表单读取的无障碍字段，实际日期交互由 zhihao DatePicker 承担。 */}
                            <input
                                aria-label="兑换有效期"
                                className="sr-only"
                                type="text"
                                value={form.expirationDate}
                                onChange={event => update("expirationDate", event.target.value)}
                                readOnly={false}
                            />
                        </Form.Item>
                        <Form.Item className="activation-code-form__remark" label="备注">
                            <Input.TextArea
                                aria-label="备注"
                                maxLength={200}
                                rows={3}
                                showCount
                                placeholder="请输入备注"
                                value={form.remark}
                                onChange={event => update("remark", event.target.value)}
                            />
                        </Form.Item>
                    </div>
                </Form>
            </div>
            <Space className="operation-fixed-btn" size={0}>
                <Button aria-label="提交激活码" type="primary" onClick={() => void submit()}>
                    提交
                </Button>
                <Button aria-label="提交并下载激活码" onClick={() => void submit(true)}>
                    提交并下载
                </Button>
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
        </section>
    );
}
